import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the office-under-attack start screen.
 * Three short slides (what/who/why a human is the control, the attacks to
 * watch for, key terms) shown in the reader while the 3D room downloads.
 * Content only; the reader UI lives in ui/StartReader.tsx and the art toolkit
 * in ui/readingArt.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. a working day: six moments, one of them live                      */
/* ------------------------------------------------------------------ */
interface Moment {
  time: string;
  where: string;
  what: string;
}

const DAY: Moment[] = [
  { time: "09:02", where: "Front door", what: "A visitor with no badge" },
  { time: "09:40", where: "Your desk", what: "A USB drive left behind" },
  { time: "10:15", where: "Inbox", what: "An urgent invoice request" },
  { time: "11:30", where: "Video call", what: "The CEO asks for a transfer" },
  { time: "14:00", where: "AI assistant", what: "Paste in customer data?" },
  { time: "15:20", where: "Phone", what: "A caller wants a code" },
];

function DayArt() {
  const H = 296;
  const live = 2;
  return (
    <Frame label={tx("Example: one working day")} aria={tx("A timeline of one working day with six moments where an attacker may try something, from the front door to a phone call")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Monday")}
      </text>
      <rect x="262" y="38" width="82" height="20" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="303" y="52" fontSize="10.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("6 moments")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <line x1="91.5" y1="96" x2="91.5" y2="266" stroke={C.line} />
      {DAY.map((m, i) => {
        const y = 96 + i * 34;
        const isLive = i === live;
        return (
          <g key={m.time}>
            {isLive && (
              <rect x="16.5" y={y - 16} width="327" height="32" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
            )}
            <text x="28" y={y + 4} fontSize="10.5" fontFamily={MONO} fill={isLive ? C.amber : C.muted}>
              {txv(m.time)}
            </text>
            <circle cx="91.5" cy={y} r="5" fill={C.panel} stroke={isLive ? C.amber : C.mint} strokeWidth="1.6" />
            {isLive && <circle className="sr-anim-ring" cx="91.5" cy={y} r="5" fill="none" stroke={C.amber} strokeWidth="1.5" />}
            <text x="108" y={y - 1} fontSize="11.5" fontWeight="700" fill={C.text}>
              {txv(m.where)}
            </text>
            <text x="108" y={y + 12} fontSize="10.5" fill={C.muted}>
              {txv(m.what)}
            </text>
          </g>
        );
      })}
      <text x="336" y="100" fontSize="10.5" fill={C.muted} textAnchor="end">
        {tx("You decide")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. red flags: a fake message with a scanned link, a spoofed call      */
/* ------------------------------------------------------------------ */
function FlagsArt() {
  const H = 300;
  return (
    <Frame label={tx("Example: what to check")} aria={tx("A suspicious message with a mismatched sender and link, an urgent video call from a fake CEO, and a reminder to verify on a known channel")} height={H}>
      <clipPath id="oua-link-clip">
        <rect x="29" y="118" width="200" height="16" rx="3" />
      </clipPath>

      <rect x="16.5" y="36.5" width="327" height="112" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="29" y="54" fontSize="10.5" fill={C.muted}>
        {tx("From")}
      </text>
      <text x="62" y="54" fontSize="11" fontFamily={MONO} fill={C.red}>
        accounts@northbay-payments.co
      </text>
      <line x1="17" y1="62.5" x2="343" y2="62.5" stroke={C.line} />
      <text x="29" y="81" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Urgent: invoice overdue today")}
      </text>
      <text x="29" y="98" fontSize="11" fill={C.soft}>
        {tx("Pay now to avoid suspension.")}
      </text>
      <rect x="29" y="118" width="200" height="16" rx="3" fill={C.code} />
      <text x="35" y="130" fontSize="10.5" fontFamily={MONO} fill={C.mint}>
        https://pay-northbay.example/login
      </text>
      <g clipPath="url(#oua-link-clip)">
        <rect className="sr-anim-sweep" x="29" y="118" width="60" height="16" fill={C.amber} fillOpacity="0.28" style={{ transformBox: "fill-box" }} />
      </g>
      <rect x="252" y="116" width="80" height="20" rx="10" fill={C.red} fillOpacity="0.12" stroke={C.red} strokeOpacity="0.45" />
      <text x="292" y="130" fontSize="10.5" fontWeight="700" fill={C.red} textAnchor="middle">
        {tx("Link differs")}
      </text>

      <rect x="16.5" y="160.5" width="327" height="74" rx="8" fill={C.panel2} stroke={C.line} />
      <circle cx="46" cy="197" r="16" fill={C.panel} stroke={C.line} />
      <circle cx="46" cy="192" r="5" fill="none" stroke={C.muted} strokeWidth="1.5" />
      <path d="M36 207 a10 8 0 0 1 20 0" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" />
      <text x="72" y="190" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Video call: CEO")}
      </text>
      <text x="72" y="206" fontSize="11" fill={C.soft}>
        {"\u201C"}{tx("Send it now. Keep this quiet.")}{"\u201D"}
      </text>
      <text x="72" y="222" fontSize="10.5" fill={C.muted}>
        {tx("Pressure and secrecy")}
      </text>
      <circle className="sr-anim-pulse" cx="318" cy="197" r="13" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.5" />
      <path d="M312 202 q2 -10 6 -10 q4 0 6 10" fill="none" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" transform="rotate(135 318 197)" />

      <rect x="16.5" y="248.5" width="327" height="38" rx="8" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.4" />
      <polyline points="30,267 34,271 41,262" fill="none" stroke={C.mint} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <text x="50" y="265" fontSize="12" fontWeight="700" fill={C.mint}>
        {tx("Verify on a channel you know")}
      </text>
      <text x="50" y="279" fontSize="10.5" fill={C.soft}>
        {tx("Then report it")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary: six icon chips + a report prompt                         */
/* ------------------------------------------------------------------ */
type Glyph = "tailgate" | "phish" | "deepfake" | "vish" | "ransom" | "report";

function GlyphShape({ g }: { g: Glyph }) {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "tailgate":
      return (
        <g {...s}>
          <rect x="-8" y="-8" width="9" height="16" rx="1.5" />
          <circle cx="5" cy="-3" r="2.4" />
          <path d="M1.5 6 a3.5 4 0 0 1 7 0" />
        </g>
      );
    case "phish":
      return (
        <g {...s}>
          <rect x="-9" y="-6" width="18" height="12" rx="2" />
          <polyline points="-8,-5 0,1 8,-5" />
        </g>
      );
    case "deepfake":
      return (
        <g {...s}>
          <circle cx="0" cy="0" r="8" />
          <line x1="-4" y1="-2" x2="-2" y2="-2" />
          <line x1="2" y1="-2" x2="4" y2="-2" />
          <path d="M-3.5 3.5 q3.5 2.5 7 0" />
        </g>
      );
    case "vish":
      return (
        <g {...s}>
          <path d="M-7 -6 q-1 7 6 12 q5 3 7 1 l-3 -4 l-3 1 q-3 -2 -4 -5 l1 -3 l-4 -3 z" />
          <path d="M3 -7 a6 6 0 0 1 4 4" />
        </g>
      );
    case "ransom":
      return (
        <g {...s}>
          <rect x="-6.5" y="-1.5" width="13" height="9" rx="2" />
          <path d="M-3.5 -1.5 V-4.5 a3.5 3.5 0 0 1 7 0 V-1.5" />
        </g>
      );
    case "report":
      return (
        <g {...s}>
          <line x1="-6" y1="-8" x2="-6" y2="8" />
          <path d="M-6 -7 h12 l-3 4 l3 4 h-12" />
        </g>
      );
  }
}

interface ChipSpec {
  g: Glyph;
  title: string;
  sub: string;
}

const CHIPS: ChipSpec[] = [
  { g: "tailgate", title: "Tailgating", sub: "following you in" },
  { g: "phish", title: "Phishing", sub: "fake messages" },
  { g: "deepfake", title: "Deepfake", sub: "faked face or voice" },
  { g: "vish", title: "Vishing", sub: "scam phone calls" },
  { g: "ransom", title: "Ransomware", sub: "locks your files" },
  { g: "report", title: "Incident report", sub: "tell security fast" },
];

function TermsArt() {
  const H = 296;
  return (
    <Frame label={tx("Terms at a glance")} aria={tx("Six terms shown as labelled icon chips: tailgating, phishing, deepfake, vishing, ransomware and incident report")} height={H}>
      {CHIPS.map((c, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = 16 + col * 172;
        const y = 38 + row * 62;
        return (
          <g key={c.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width="155" height="51" rx="10" fill={C.panel2} stroke={C.line} />
            <circle cx="26" cy="26" r="16" fill={C.mint} fillOpacity="0.12" />
            <g transform="translate(26 26)">
              <GlyphShape g={c.g} />
            </g>
            <text x="51" y="23" fontSize="12" fontWeight="700" fill={C.text}>
              {txv(c.title)}
            </text>
            <text x="51" y="39" fontSize="10.5" fill={C.muted}>
              {txv(c.sub)}
            </text>
          </g>
        );
      })}
      <rect x="16.5" y="232.5" width="327" height="50" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="36" cy="257.5" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="36" cy="257.5" r="1.8" fill={C.amber} />
      <text x="52" y="254" fontSize="12" fontWeight="700" fill={C.amber}>
        {tx("Not sure? Report it anyway")}
      </text>
      <text x="52" y="270" fontSize="10.5" fill={C.soft}>
        {tx("A quick report beats a quiet guess.")}
      </text>
    </Frame>
  );
}

export const OFFICE_UNDER_ATTACK_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "One working day, many ways in.",
    illustration: DayArt,
    caption: "Every attack ends with a person making a choice.",
    sections: [
      {
        h: "What this is about",
        body: "A busy Monday at a finance office. You meet a visitor, a USB drive, a full inbox, a CEO on video, an AI tool and a phone call.",
      },
      {
        h: "Who it\u2019s for",
        body: "All staff. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Locks, filters and scanners stop a lot, but not everything. The last step is often a person: hold the door, open the file, pay the invoice, share the code. That person is you.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "Different channels, the same tricks.",
    illustration: FlagsArt,
    caption: "Slow down, then check another way.",
    sections: [
      {
        h: "People who seem to belong",
        body: "A visitor with full hands follows you through a secured door. A USB drive left on a desk is bait. Check before you help or plug in.",
      },
      {
        h: "Messages built to rush you",
        body: "Fake invoices and urgent requests push you to click before you think. Check the sender and the real link first.",
      },
      {
        h: "Faces and voices that aren\u2019t real",
        body: "A cloned voice or a deepfake CEO can ask for money or a code. Confirm on a number you already know, not the one they give you.",
      },
      {
        h: "Data in the wrong place",
        body: "Customer records pasted into an AI tool leave your control. Share only what you are allowed to.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Tailgating", body: "Slipping through a secured door behind someone who has a badge." },
      { h: "Phishing", body: "A fake message that tricks you into clicking, signing in or paying." },
      { h: "Deepfake", body: "A video or voice made or altered by AI to pass as a real person." },
      { h: "Vishing", body: "Voice phishing: a phone call that pressures you to give up information or money." },
      { h: "Ransomware", body: "Malware that locks your files and demands payment to unlock them." },
      { h: "Incident report", body: "A clear, fast note to security saying what you saw, when, and what you did." },
    ],
  },
];
